import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { API_URL } from '../utils/config';

// User info interface
export interface UserInfo {
  id: string;
  email: string;
  role: string;
  roles: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
  permissions: string[];
}

// Current user fetcher with authentication
export const useCurrentUser = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<UserInfo | null, Error>({
    queryKey: ['currentUser'],
    queryFn: async (): Promise<UserInfo | null> => {
      try {
        if (accounts.length === 0) {
          console.warn('No authenticated user');
          return null;
        }

        const url = `${API_URL}/api/general/me`;
        const response = await authenticatedFetch(url, { timeout: 30000 }); // 30 second timeout
        
        if (!response) {
          console.error('Failed to fetch user info: No response from authenticatedFetch');
          return null;
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch user info: HTTP ${response.status} ${response.statusText}`);
          const text = await response.text();
          console.error('Response body:', text);
          return null;
        }

        const data = await response.json();
        return data.data || null;
      } catch (error) {
        console.error('Current user fetch error:', error);
        return null;
      }
    },
    enabled: accounts.length > 0,
    // Disable caching for current user – always fetch when needed
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// Dashboard stats fetcher with authentication
export const useDashboardStats = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      try {
        // Only attempt authenticated fetch if user is logged in
        if (accounts.length === 0) {
          console.warn('No authenticated user, using mock data');
          return getMockDashboardStats();
        }

        const response = await authenticatedFetch(`${API_URL}/api/sandbox/dashboard/stats`);
        
        if (!response) {
          console.warn('Authenticated fetch failed, using mock data');
          return getMockDashboardStats();
        }

        if (!response.ok) {
          console.warn('API response not ok, using mock data');
          return getMockDashboardStats();
        }

        const data = await response.json();
        return data.data || data;
      } catch (error) {
        console.warn('Dashboard stats fetch error, using mock data:', error);
        return getMockDashboardStats();
      }
    },
    enabled: true, // Always enabled, will use mock data if not authenticated
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: false, // Don't retry on failure, fallback to mock data
  });
};

// User analytics fetcher with authentication
export const useUserAnalytics = (dateRange?: {
  startDate?: string;
  endDate?: string;
}) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['userAnalytics', dateRange],
    queryFn: async () => {
      try {
        // Only attempt authenticated fetch if user is logged in
        if (accounts.length === 0) {
          console.warn('No authenticated user, using mock data');
          return getMockUserAnalytics();
        }

        const params = new URLSearchParams();
        if (dateRange?.startDate)
          params.append('startDate', dateRange.startDate);
        if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

        const url = `${API_URL}/api/analytics/users${params.toString() ? '?' + params.toString() : ''}`;
        const response = await authenticatedFetch(url);

        if (!response) {
          console.warn('Authenticated fetch failed, using mock data');
          return getMockUserAnalytics();
        }

        if (!response.ok) {
          console.warn('API response not ok, using mock data');
          return getMockUserAnalytics();
        }

        const data = await response.json();
        return data.data || data;
      } catch (error) {
        console.warn('User analytics fetch error, using mock data:', error);
        return getMockUserAnalytics();
      }
    },
    enabled: true, // Always enabled, will use mock data if not authenticated
    retry: false, // Don't retry on failure, fallback to mock data
  });
};

// Admin dashboard counts fetcher with authentication
export const useAdminDashboardCounts = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['adminDashboardCounts'],
    queryFn: async () => {
      try {
        // Only attempt authenticated fetch if user is logged in
        if (accounts.length === 0) {
          return { pendingMarketReports: 0, submittedTrendReports: 0 };
        }

        const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/dashboard-counts`);

        if (!response || !response.ok) {
          return { pendingMarketReports: 0, submittedTrendReports: 0 };
        }

        const data = await response.json();
        return data.data || { pendingMarketReports: 0, submittedTrendReports: 0 };
      } catch (error) {
        console.warn('Admin dashboard counts fetch error:', error);
        return { pendingMarketReports: 0, submittedTrendReports: 0 };
      }
    },
    enabled: accounts.length > 0,
    staleTime: 60000, // Cache for 1 minute
    retry: false,
  });
};

// Mock data fallbacks
const getMockDashboardStats = () => ({
  totalProjects: 45,
  activeConsultations: 12,
  completedProjects: 33,
  revenueThisMonth: 125000,
  clientSatisfactionScore: 4.8,
  teamUtilization: 87,
  recentActivities: [
    {
      id: '1',
      type: 'project_started',
      description: 'New hotel valuation project in Prague',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      client: 'Central European Hotels',
    },
    {
      id: '2',
      type: 'consultation_completed',
      description: 'Market analysis consultation completed',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      client: 'Alpine Resorts Group',
    },
    {
      id: '3',
      type: 'report_delivered',
      description: 'Feasibility study report delivered',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      client: 'Mediterranean Hotels Ltd',
    },
  ],
  upcomingDeadlines: [
    {
      id: '1',
      projectName: 'Vienna Hotel District Analysis',
      deadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'on_track',
    },
    {
      id: '2',
      projectName: 'Luxury Resort Valuation - Santorini',
      deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'at_risk',
    },
  ],
});

const getMockUserAnalytics = () => ({
  totalUsers: 1250,
  activeUsers: 875,
  newUsers: 45,
  userGrowthRate: 12.5,
  usersByRegion: [
    { region: 'North America', count: 450, percentage: 36 },
    { region: 'Europe', count: 380, percentage: 30.4 },
    { region: 'Asia Pacific', count: 290, percentage: 23.2 },
  ],
  userEngagement: {
    averageSessionTime: 24.5,
    pagesPerSession: 3.2,
    bounceRate: 0.35,
  },
  topUserActions: [
    { action: 'view_hotel', count: 2340 },
    { action: 'search_hotels', count: 1890 },
    { action: 'contact_request', count: 234 },
  ],
});
